import { withDevtools, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { computed, inject, InjectionToken } from '@angular/core';
import { IdentService, PersonEntities } from '@api';
import { getErrorText } from '@core/utils/error.util';
import { LabelValueInterface } from '@features/history/types/filter.types';
import { withLoader } from '@features/loader/loader.service';
import { ErrorResponse } from '@models/core/error-response.model';
import { SsoStatus } from '@models/shared/sso-status.model';
import { EntityModel, PersonModel, UserState } from '@models/user/user.model';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withRequestStatus } from '@store/status-utils/request-status/request-status.feature';
import { catchError, of, pipe } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { InstytucjaObowiazana } from 'src/app/shared/types/institutions.types';

const initialState: UserState = {
  firstName: null,
  lastName: null,
  entities: [],
  selectedEntity: null,
  logged: false,
  loaded: false,
  error: null!,
  expirationDate: null,
};

const USER_STATE = new InjectionToken<UserState>('UserState', {
  providedIn: 'root',
  factory: () => initialState,
});

export const UserStore = signalStore(
  { providedIn: 'root' },
  withDevtools('User'),
  withState(() => inject(USER_STATE)),
  withRequestStatus(),
  withStorageSync('auth'),
  withComputed((store) => ({
    hasGoAmlAccount: computed(
      () =>
        !!store.entities() &&
        store.entities().length > 0 &&
        store.entities().some((entity) => entity.persons.some((person) => person.isActive))
    ),
    isUserLoggedIn: computed(() => !!store.logged()),
    entities: computed(() =>
      store.entities().map((entity) => ({
        ...entity,
        persons: entity.persons.map((person) => ({
          ...person,
          roles: person.roles
            ?.split(',')
            .map((role) => role.trim().toLowerCase())
            .join(', '),
        })),
      }))
    ),
    entitiesForm: computed(() => store.entities()),

    loaded: computed(() => store.loaded()),
    fullName: computed(() => `${store.firstName() ?? ''} ${store.lastName() ?? ''}`),
    error: computed(() => store.error()),
  })),
  withMethods((store, apiService = inject(AuthService), ident = inject(IdentService)) => ({
    getSsoStatus: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, store.setPending());

          return withLoader(apiService.getStatus()).pipe(
            tapResponse(
              (response: SsoStatus) => {
                if (!response?.expires) return;

                const nowInSeconds = Math.floor(Date.now() / 1000);
                const expiresInSeconds = response.expires;

                const remainingSeconds = expiresInSeconds - nowInSeconds;

                const expirationDate = Date.now() + remainingSeconds * 1000;

                patchState(store, store.setFulfilled(), {
                  expirationDate,
                });
              },
              (err: ErrorResponse) => {
                patchState(store, { logged: false }, store.setError(getErrorText(err)));
              }
            )
          );
        })
      )
    ),
    getUserData: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, store.setPending());

          return withLoader(ident.identGetEntities()).pipe(
            tap((data: PersonEntities) => {
              patchState(
                store,
                {
                  ...(data as UserState),
                  logged: true,
                  loaded: true,
                  error: null!,
                  selectedEntity: null,
                },
                store.setFulfilled()
              );
            }),
            catchError((error: ErrorResponse) => {
              patchState(
                store,
                {
                  ...initialState,
                  logged: false,
                  loaded: true,
                  error: error,
                  selectedEntity: null,
                },
                store.setError(error.message)
              );
              return of(error);
            })
          );
        })
      )
    ),

    clearState(): void {
      patchState(store, { ...initialState });
    },

    getInstitutionByNipOrReId(nipOrReId: string | number | any): EntityModel | undefined {
      if (!nipOrReId) return undefined;

      const searchValue = typeof nipOrReId === 'object' ? nipOrReId.nip || nipOrReId.reId : nipOrReId;

      return store
        .entities()
        ?.find((el: EntityModel) => el.nip === String(searchValue) || el.reId === Number(searchValue));
    },

    getALlInstitutionByNipOrReId(nipOrReId: string | number | any): EntityModel[] | undefined {
      if (!nipOrReId) return undefined;

      const searchValue = typeof nipOrReId === 'object' ? nipOrReId.nip || nipOrReId.reId : nipOrReId;

      return store
        .entities()
        ?.filter((el: EntityModel) => el.nip === String(searchValue) || el.reId === Number(searchValue));
    },

    getEntityById: rxMethod<number>(
      pipe(
        switchMap((id: number) => {
          patchState(store, store.setPending());

          const foundEntity = store.entities().find((e) => e.reId === id) ?? null;

          patchState(store, { selectedEntity: foundEntity }, store.setFulfilled());

          return of(foundEntity);
        })
      )
    ),

    getUsersByInstitution(instId: number) {
      const institution = store.entities().find((e) => e.reId === instId);
      return institution ? institution.persons : [];
    },

    getUsersByUserCode(userCode: string) {
      return (
        store
          ?.entities()
          ?.flatMap((entity) => entity.persons || [])
          .find((person) => person.userCode === userCode) || null
      );
    },

    getAgencyTypeByInstitutionId(nipOrReId: string) {
      return store
        ?.entities()
        ?.find((el: EntityModel) => el.nip === nipOrReId || el.reId === Number(nipOrReId))
        ?.agencyBusinessType;
    },

    getInstitutionsByUserCode(userCode: string) {
      return store.entities().filter((e) => e.persons.some((p) => p.userCode === userCode));
    },

    getInstitutions(): InstytucjaObowiazana[] {
      return store.entities() as unknown as InstytucjaObowiazana[];
    },

    getInstitutionsAsLabelValue(): LabelValueInterface[] {
      return store.entities()?.map((el: EntityModel) => {
        return { label: `(${el.nip}) ${el?.name}`, value: el };
      });
    },

    hasPersonRole(person: PersonModel, roles: string[]) {
      const personRoles = person.roles?.split(',').map((r) => r.trim());

      return roles.every((role) => personRoles?.includes(role.toLowerCase()));
    },

    authorizeByInstitution(nipOrReId: string, roles: string[]): boolean {
      return store
        .entities()
        .filter((entity: EntityModel) => entity.nip === nipOrReId || entity.reId === Number(nipOrReId))
        .some((entity: EntityModel) =>
          entity.persons
            .filter((person) => person.isActive)
            .some((person) => {
              return this.hasPersonRole(person, roles);
            })
        );
    },

    authorizeByUserCodeInInstitution(nipOrReId: string, userCode: string, roles: string[]): boolean {
      return store
        .entities()
        .filter((entity: EntityModel) => entity.nip === nipOrReId || entity.reId === Number(nipOrReId))
        .some((entity: EntityModel) =>
          entity.persons.some((person) => {
            if (person.userCode !== userCode) {
              return false;
            }
            return this.hasPersonRole(person, roles);
          })
        );
    },
  })),

  withHooks({
    onInit: (store) => {
      store.getSsoStatus();
    },
  })
);
